// react
import type { ComponentType, ForwardedRef } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
// libraries
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  alpha,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';

// The installed DateRangePicker's type defs no longer expose the legacy
// startText/endText/renderInput props used below (manual-input fallback
// mode, ported as-is from the original stepper); cast to bypass that.
const LegacyDateRangePicker = DateRangePicker as unknown as ComponentType<
  Record<string, unknown>
>;

// RTK Query errors are FetchBaseQueryError | SerializedError; only the
// former has a `data` field, so narrow safely without using `any`.
const getErrorData = (err: unknown): unknown =>
  err && typeof err === 'object' && 'data' in err
    ? (err as { data: unknown }).data
    : undefined;

// api
import { useLazyGetEnergyDataSpecificQuery } from '@/api/energy-analysis/energyDataApiSlice';

// feature-components
import EnergySumChartInteractive from '@/features/energyxplorer/components/highcharts/EnergySumChartInteractive';
import DateRangeWithGenerate from '@/features/energyxplorer/components/initiatives/DateRangeWithGenerate';
import ToolMenu from '@/features/energyxplorer/components/initiatives/InitiativeGraphTool';
import TimestampSummaryIndicator from '@/features/energyxplorer/components/timestamps-summary/TimestampSummaryIndicator';

// components
import { showSnackbar } from '@/redux/snackbarSlice';
import {
  getTimestampSummaryClampBounds,
  getTimestampSummaryValidationStatus,
} from '@/utils/getTimestampSummaryValidationStatus';

// relative
import type { InitiativeFormValues } from './initiativeForm.schema';

import { DateTime } from 'luxon';

const ZONE = 'Europe/Copenhagen';

const fmt = (ms: number | null | undefined) =>
  typeof ms === 'number'
    ? DateTime.fromMillis(ms, { zone: ZONE }).toFormat('dd/MM/yyyy')
    : '—';

export type InitialInitiativeFetch = {
  nodeIds: string[];
  start: number;
  end: number;
};

export type InitiativePeriodsSectionHandle = {
  // Called when the source selection changes in a way that won't blur the
  // input (removing a chip, clearing the field) or when the picker loses
  // focus, so the graph refreshes automatically. Takes the up-to-date node
  // ids explicitly rather than relying on this component's own `useWatch`
  // value, since that value may not have re-rendered yet at the moment the
  // caller invokes this (e.g. right after removing a chip).
  triggerAutoFetch: (nodeIds: string[]) => void;
};

type InitiativePeriodsSectionProps = {
  control: Control<InitiativeFormValues>;
  setValue: UseFormSetValue<InitiativeFormValues>;
  energyGroupId?: number;
  accountId?: number | string;
  totalNodesInGroup: number;
  disabled?: boolean;
  initialFetch?: InitialInitiativeFetch | null;
};

function InitiativePeriodsSection(
  {
    control,
    setValue,
    energyGroupId,
    accountId,
    totalNodesInGroup,
    disabled,
    initialFetch,
  }: InitiativePeriodsSectionProps,
  ref: ForwardedRef<InitiativePeriodsSectionHandle>,
) {
  const { t } = useTranslation(['forms', 'energyxplorer-initiatives-tab']);
  const dispatch = useDispatch();

  const energyTreeNodes = useWatch({ control, name: 'energyTreeNodes' });
  const baselineStart = useWatch({ control, name: 'baselineStart' });
  const baselineEnd = useWatch({ control, name: 'baselineEnd' });
  const comparisonStart = useWatch({ control, name: 'comparisonStart' });
  const comparisonEnd = useWatch({ control, name: 'comparisonEnd' });

  const now = DateTime.now().setZone(ZONE);
  const [startDt, setStartDt] = useState(
    initialFetch
      ? DateTime.fromMillis(initialFetch.start, { zone: ZONE })
      : now.minus({ weeks: 4 }).startOf('day'),
  );
  const [endDt, setEndDt] = useState(
    initialFetch ? DateTime.fromMillis(initialFetch.end, { zone: ZONE }) : now,
  );
  const startMs = startDt?.toMillis();
  const endMs = endDt?.toMillis();
  const isRangeValid =
    typeof startMs === 'number' && typeof endMs === 'number' && endMs > startMs;

  const [useManualInputs, setUseManualInputs] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [lastFetchedNodeIds, setLastFetchedNodeIds] = useState<string[]>(
    initialFetch?.nodeIds ?? [],
  );

  const [fetchEnergyData, { data, isFetching, isError, error }] =
    useLazyGetEnergyDataSpecificQuery();

  // One-time fetch of the pre-existing baseline/comparison window when
  // editing an existing initiative (external system sync - a network
  // request - so a mount effect is appropriate here).
  const didInitialFetch = useRef(false);
  useEffect(() => {
    if (didInitialFetch.current || !initialFetch) return;
    didInitialFetch.current = true;
    fetchEnergyData({
      accountId,
      energyGroupId,
      start: initialFetch.start,
      end: initialFetch.end,
      node_ids: initialFetch.nodeIds,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timestampSummary = data?.EnergySummary?.TimestampsSummary || null;

  // Memoized so it can be safely listed as a useImperativeHandle dependency
  // below (triggerAutoFetch needs a stable reference to call into it).
  const runEnergyFetch = useCallback(
    async (nodeIds: string[], fetchStartMs: number, fetchEndMs: number) => {
      if (!energyGroupId || nodeIds.length === 0) return;

      const result = await fetchEnergyData({
        accountId,
        energyGroupId,
        start: fetchStartMs,
        end: fetchEndMs,
        node_ids: nodeIds,
      });
      setLastFetchedNodeIds(nodeIds);

      const summary = result?.data?.EnergySummary?.TimestampsSummary || null;
      const validation = getTimestampSummaryValidationStatus(
        { start: fetchStartMs, end: fetchEndMs },
        summary,
      );

      if (validation.isOutOfBounds) {
        setStartDt(validation.adjustedRange[0]);
        setEndDt(validation.adjustedRange[1]);
        dispatch(
          showSnackbar({
            message: t('dialogs.shared.messages.range_adjusted', {
              ns: 'energyxplorer-initiatives-tab',
            }),
            severity: 'info',
          }),
        );
      }
    },
    [
      energyGroupId,
      accountId,
      fetchEnergyData,
      dispatch,
      t,
      setStartDt,
      setEndDt,
    ],
  );

  const canGenerate = Boolean(
    energyGroupId && energyTreeNodes.length > 0 && isRangeValid,
  );

  // Exposes an imperative trigger so a sibling component (the source
  // selector) can kick off the fetch on blur/removal, without lifting all
  // of this section's fetch state up to the parent form.
  useImperativeHandle(
    ref,
    () => ({
      triggerAutoFetch: (nodeIds: string[]) => {
        if (useManualInputs || !isRangeValid) return;
        runEnergyFetch(nodeIds, startMs, endMs);
      },
    }),
    [useManualInputs, isRangeValid, startMs, endMs, runEnergyFetch],
  );

  const nodesChangedSinceFetch =
    Boolean(data) &&
    lastFetchedNodeIds.length > 0 &&
    (energyTreeNodes.length !== lastFetchedNodeIds.length ||
      [...energyTreeNodes].sort().join(',') !==
        [...lastFetchedNodeIds].sort().join(','));

  const selections = {
    baseline: {
      start: baselineStart?.toMillis() ?? null,
      end: baselineEnd?.toMillis() ?? null,
    },
    comparison: {
      start: comparisonStart?.toMillis() ?? null,
      end: comparisonEnd?.toMillis() ?? null,
    },
  };

  const clampSelection = useCallback(
    (selection?: { start?: number | null; end?: number | null } | null) => {
      if (!selection) {
        return { selection: {}, adjusted: false };
      }

      const bounds = getTimestampSummaryClampBounds(timestampSummary);
      if (!bounds.isValid || bounds.isInverted) {
        return { selection, adjusted: false };
      }

      let adjusted = false;
      const clampValue = (value?: number | null) => {
        if (typeof value !== 'number') return value ?? null;

        const dt = DateTime.fromMillis(value, { zone: ZONE });
        if (!dt.isValid) return value;
        if (dt < bounds.maxEarliest) {
          adjusted = true;
          return bounds.maxEarliest.toMillis();
        }
        if (dt > bounds.minLatest) {
          adjusted = true;
          return bounds.minLatest.toMillis();
        }
        return value;
      };

      return {
        selection: {
          start: clampValue(selection.start),
          end: clampValue(selection.end),
        },
        adjusted,
      };
    },
    [timestampSummary],
  );

  const applySelections = (next: {
    baseline?: { start?: number | null; end?: number | null } | null;
    comparison?: { start?: number | null; end?: number | null } | null;
  }) => {
    const { selection: baseline = {}, adjusted: baselineAdjusted } =
      clampSelection(next?.baseline);
    const { selection: comparison = {}, adjusted: comparisonAdjusted } =
      clampSelection(next?.comparison);

    if (baselineAdjusted || comparisonAdjusted) {
      dispatch(
        showSnackbar({
          message: t('dialogs.shared.messages.range_adjusted', {
            ns: 'energyxplorer-initiatives-tab',
          }),
          severity: 'info',
        }),
      );
    }

    setValue(
      'baselineStart',
      baseline.start
        ? DateTime.fromMillis(baseline.start, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'baselineEnd',
      baseline.end ? DateTime.fromMillis(baseline.end, { zone: ZONE }) : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'comparisonStart',
      comparison.start
        ? DateTime.fromMillis(comparison.start, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'comparisonEnd',
      comparison.end
        ? DateTime.fromMillis(comparison.end, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
  };

  useEffect(() => {
    if (useManualInputs || !timestampSummary) return;

    const { selection: normalizedBaseline = {}, adjusted: baselineAdjusted } =
      clampSelection(selections.baseline);
    const {
      selection: normalizedComparison = {},
      adjusted: comparisonAdjusted,
    } = clampSelection(selections.comparison);

    if (!baselineAdjusted && !comparisonAdjusted) {
      return;
    }

    setValue(
      'baselineStart',
      normalizedBaseline.start
        ? DateTime.fromMillis(normalizedBaseline.start, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'baselineEnd',
      normalizedBaseline.end
        ? DateTime.fromMillis(normalizedBaseline.end, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'comparisonStart',
      normalizedComparison.start
        ? DateTime.fromMillis(normalizedComparison.start, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );
    setValue(
      'comparisonEnd',
      normalizedComparison.end
        ? DateTime.fromMillis(normalizedComparison.end, { zone: ZONE })
        : null,
      { shouldDirty: true, shouldValidate: true },
    );

    dispatch(
      showSnackbar({
        message: t('dialogs.shared.messages.range_adjusted', {
          ns: 'energyxplorer-initiatives-tab',
        }),
        severity: 'info',
      }),
    );
  }, [
    useManualInputs,
    timestampSummary,
    selections.baseline,
    selections.comparison,
    clampSelection,
    setValue,
    dispatch,
    t,
  ]);

  const clearBaseline = () =>
    applySelections({ baseline: {}, comparison: selections.comparison });
  const clearComparison = () =>
    applySelections({ baseline: selections.baseline, comparison: {} });

  const pointsForChart = Array.isArray(data?.EnergyData) ? data.EnergyData : [];

  return (
    <Stack spacing={2}>
      {/* <Typography variant="subtitle1" fontWeight={700}>
        {t('createInitiative.periods.sectionLabel', { ns: 'forms' })}
      </Typography> */}
      {!isRangeValid && (
        <Typography variant="caption" color="error">
          {t('createInitiative.periods.invalidTimeRange', {
            ns: 'forms',
          })}
        </Typography>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={useManualInputs}
            disabled={disabled}
            onChange={() => setUseManualInputs((prev) => !prev)}
          />
        }
        label={t('createInitiative.periods.useManualInputs', {
          ns: 'forms',
        })}
      />

      {useManualInputs ? (
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('createInitiative.periods.baselinePeriodLabel', {
              ns: 'forms',
            })}
          </Typography>
          <LegacyDateRangePicker
            value={[baselineStart, baselineEnd]}
            onChange={(newValue: [DateTime | null, DateTime | null]) => {
              setValue('baselineStart', newValue[0], {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('baselineEnd', newValue[1], {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            renderInput={(
              startProps: Record<string, unknown>,
              endProps: Record<string, unknown>,
            ) => (
              <>
                <TextField
                  {...startProps}
                  label={t('createInitiative.periods.baselineStart', {
                    ns: 'forms',
                  })}
                />
                <TextField
                  {...endProps}
                  label={t('createInitiative.periods.baselineEnd', {
                    ns: 'forms',
                  })}
                />
              </>
            )}
          />

          <Typography variant="subtitle2" color="textSecondary">
            {t('createInitiative.periods.comparisonPeriodLabel', {
              ns: 'forms',
            })}
          </Typography>
          <LegacyDateRangePicker
            value={[comparisonStart, comparisonEnd]}
            onChange={(newValue: [DateTime | null, DateTime | null]) => {
              setValue('comparisonStart', newValue[0], {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue('comparisonEnd', newValue[1], {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            renderInput={(
              startProps: Record<string, unknown>,
              endProps: Record<string, unknown>,
            ) => (
              <>
                <TextField
                  {...startProps}
                  label={t('createInitiative.periods.comparisonStart', {
                    ns: 'forms',
                  })}
                  helperText={t(
                    'dialogs.shared.messages.comparison_after_baseline',
                    { ns: 'energyxplorer-initiatives-tab' },
                  )}
                />
                <TextField
                  {...endProps}
                  label={t('createInitiative.periods.comparisonEnd', {
                    ns: 'forms',
                  })}
                />
              </>
            )}
          />
        </Stack>
      ) : (
        <Box sx={{ minHeight: 420 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            flexWrap="wrap"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                variant="filled"
                label={
                  selections.baseline.start && selections.baseline.end
                    ? `${t('common.baseline_period', { ns: 'energyxplorer-initiatives-tab' })}: ${fmt(selections.baseline.start)} -> ${fmt(selections.baseline.end)}`
                    : t('common.baseline_period', {
                        ns: 'energyxplorer-initiatives-tab',
                      })
                }
                onDelete={clearBaseline}
                sx={{
                  height: 'auto',
                  borderRadius: 2,
                  color: 'info.main',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.1),
                }}
              />
              <Chip
                variant="filled"
                label={
                  selections.comparison.start && selections.comparison.end
                    ? `${t('common.comparison_period', { ns: 'energyxplorer-initiatives-tab' })}: ${fmt(selections.comparison.start)} -> ${fmt(selections.comparison.end)}`
                    : t('common.comparison_period', {
                        ns: 'energyxplorer-initiatives-tab',
                      })
                }
                onDelete={clearComparison}
                sx={{
                  height: 'auto',
                  borderRadius: 2,
                  color: 'success.main',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.success.main, 0.1),
                }}
              />
            </Stack>

            <DateRangeWithGenerate
              startDt={startDt}
              endDt={endDt}
              canGenerate={canGenerate}
              disabled={disabled}
              onChangeRange={(newStart: DateTime, newEnd: DateTime) => {
                setStartDt(newStart.setZone(ZONE));
                setEndDt(newEnd.setZone(ZONE));
              }}
              onGenerate={(genStartMs: number, genEndMs: number) =>
                runEnergyFetch(energyTreeNodes, genStartMs, genEndMs)
              }
              zone={ZONE}
              locale="da"
              generateLabel={t('createInitiative.periods.generateGraph', {
                ns: 'forms',
              })}
            />
          </Stack>

          <TimestampSummaryIndicator
            globalTimeRange={{ start: startMs, end: endMs }}
            timestampSummary={timestampSummary}
          />

          {!data && !isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
              spacing={1}
            >
              <Typography variant="body1">
                {t('createInitiative.periods.introMessage', {
                  ns: 'forms',
                })}{' '}
                <strong>
                  {t('createInitiative.periods.generateGraph', {
                    ns: 'forms',
                  })}
                </strong>
              </Typography>
              {totalNodesInGroup === 0 && (
                <Typography variant="body2">
                  {t('createInitiative.periods.noNodesFound', {
                    ns: 'forms',
                  })}
                </Typography>
              )}
            </Stack>
          )}

          {isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400 }}
            >
              {t('createInitiative.periods.loading', { ns: 'forms' })}
            </Stack>
          )}

          {isError && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                height: 400,
                color: '#b00020',
                textAlign: 'center',
                whiteSpace: 'pre-wrap',
              }}
            >
              {t('createInitiative.periods.failedToLoad', {
                ns: 'forms',
              })}
              {getErrorData(error)
                ? `: ${JSON.stringify(getErrorData(error))}`
                : ''}
            </Stack>
          )}

          {!isFetching && !isError && pointsForChart.length > 0 && (
            <Box
              sx={{
                opacity: nodesChangedSinceFetch ? 0.4 : 1,
                filter: nodesChangedSinceFetch ? 'grayscale(70%)' : 'none',
                pointerEvents: nodesChangedSinceFetch ? 'none' : 'auto',
                transition: 'opacity 0.2s ease, filter 0.2s ease',
              }}
            >
              <EnergySumChartInteractive
                key={[...energyTreeNodes].sort().join(',')}
                points={pointsForChart}
                selections={selections}
                activeTool={activeTool}
                onChange={applySelections}
                title=""
                height={380}
              />
            </Box>
          )}

          {!isFetching && !isError && data && pointsForChart.length === 0 && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: 400, opacity: 0.7 }}
            >
              <Typography>
                {t('createInitiative.periods.noDataReturned', {
                  ns: 'forms',
                })}
              </Typography>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            justifyContent="center"
          >
            <ToolMenu activeTool={activeTool} setActiveTool={setActiveTool} />
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

const ForwardedInitiativePeriodsSection = forwardRef(InitiativePeriodsSection);

export default ForwardedInitiativePeriodsSection;
